import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { 
  Phone, 
  Mail, 
  Clock,
  Shield,
  CheckCircle2,
  Users,
  DollarSign,
  Plane,
  MapPin,
  Car,
  Star,
  Award,
  Headphones,
  Globe,
  ArrowRight,
  Palmtree,
  Sparkles,
  Crown
} from 'lucide-react';
import { TransferBookingForm, SearchResultData } from '@/components/TransferBookingForm';
import { useTestimonials } from '@/hooks/useTestimonials';
import { useTours } from '@/hooks/useTours';
import { TourCard } from '@/components/TourCard';
import Layout from '@/components/Layout';
import { useHeroSlides } from '@/hooks/useHeroSlides';
import { useSiteConfig } from '@/hooks/useSiteConfig';
import { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import bavaroBeach from '@/assets/bavaro-beach.jpg';
import GoldenParticles from '@/components/GoldenParticles';
import WaveGoldDivider from '@/components/WaveGoldDivider';
import VIPBadge from '@/components/VIPBadge';
import AnimatedSection from '@/components/AnimatedSection';
import AnimatedCounter from '@/components/AnimatedCounter';
import VehicleSelectionSection from '@/components/VehicleSelectionSection';

const HomePage = () => {
  const { testimonials } = useTestimonials();
  const { tours } = useTours();
  const { data: heroSlides, isLoading: slidesLoading } = useHeroSlides();
  const { getConfig } = useSiteConfig();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [searchResults, setSearchResults] = useState<SearchResultData | null>(null);
  
  const featuredTestimonials = testimonials.filter(t => t.is_featured).slice(0, 3);
  const featuredTours = tours.filter(t => t.is_active && t.is_featured).slice(0, 4);

  useEffect(() => {
    if (heroSlides && heroSlides.length > 0) {
      const timer = setInterval(() => {
        setCurrentSlide(prev => (prev + 1) % heroSlides.length);
      }, 6000);
      return () => clearInterval(timer);
    }
  }, [heroSlides]);

  const nextSlide = () => {
    if (heroSlides) {
      setCurrentSlide(prev => (prev + 1) % heroSlides.length);
    }
  };

  const prevSlide = () => {
    if (heroSlides) {
      setCurrentSlide(prev => (prev - 1 + heroSlides.length) % heroSlides.length);
    }
  };

  const airports = [
    {
      code: "PUJ",
      name: "Punta Cana International Airport",
      description: "Main gateway to Punta Cana and Bávaro",
      popular: true,
      transferTime: "15-45 min"
    },
    {
      code: "SDQ",
      name: "Las Américas International Airport",
      description: "Serving Santo Domingo and surrounding areas",
      popular: false,
      transferTime: "2-3 hours"
    },
    {
      code: "LRM",
      name: "La Romana International Airport",
      description: "Gateway to La Romana and Casa de Campo",
      popular: false,
      transferTime: "45-60 min"
    },
    {
      code: "AZS",
      name: "Samaná El Catey International Airport",
      description: "Access to Samaná Peninsula",
      popular: false,
      transferTime: "2.5-3 hours"
    },
    {
      code: "POP",
      name: "Puerto Plata International Airport",
      description: "North coast access",
      popular: false,
      transferTime: "3.5-4 hours"
    },
    {
      code: "STI",
      name: "Cibao International Airport",
      description: "Santiago and Cibao region",
      popular: false,
      transferTime: "4+ hours"
    }
  ];

  if (slidesLoading) {
    return <Layout><div className="min-h-screen flex items-center justify-center bg-vip-dark text-primary">
      <div className="flex items-center gap-3">
        <Crown className="w-8 h-8 animate-pulse-glow" />
        <span className="text-xl font-semibold">Loading VIP Experience...</span>
      </div>
    </div></Layout>;
  }

  return (
    <Layout>
      <div className="min-h-screen">
        {/* Hero Section with VIP Dark Theme */}
        <section className="relative min-h-[100vh] flex items-center overflow-hidden">
          {/* Background Slides */}
          {heroSlides?.map((slide, index) => (
            <div
              key={slide.id}
              className={`absolute inset-0 bg-cover bg-center transition-opacity duration-1000 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0'
              }`}
              style={{ backgroundImage: `url(${slide.image_url})` }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-black/95 via-black/80 to-primary/20" />
            </div>
          ))}
          
          {/* Golden Particles */}
          <GoldenParticles count={25} />
          
          {/* Navigation Arrows */}
          {heroSlides && heroSlides.length > 1 && (
            <>
              <button 
                onClick={prevSlide}
                className="absolute left-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full glass-gold text-primary hover:bg-primary/20 transition-all duration-300 hover:scale-110"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button 
                onClick={nextSlide}
                className="absolute right-4 top-1/2 -translate-y-1/2 z-20 p-3 rounded-full glass-gold text-primary hover:bg-primary/20 transition-all duration-300 hover:scale-110"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}

          {/* Slide Indicators */}
          {heroSlides && heroSlides.length > 1 && (
            <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex space-x-3">
              {heroSlides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    index === currentSlide ? 'bg-primary scale-125 shadow-glow' : 'bg-white/30 hover:bg-primary/50'
                  }`}
                />
              ))}
            </div>
          )}
          
          {/* Split-Screen Hero Content */}
          <div className="relative z-10 w-full py-24 lg:py-32">
            <div className="container mx-auto px-4 lg:px-8">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-12 lg:gap-8">
                {/* Left Side - Text Content (55% width on desktop) */}
                <div className="text-white animate-fade-in lg:w-[55%] lg:pr-8">
                  <VIPBadge variant="exclusive" size="lg" icon="crown" className="mb-6" animated>
                    VIP Transfer Service
                  </VIPBadge>
                  
                  <h1 className="text-4xl md:text-5xl lg:text-5xl xl:text-6xl font-bold mb-6 leading-tight">
                    <span className="text-white">Your Reliable</span>
                    <br />
                    <span className="text-gradient-gold">Worldwide</span>
                    <br />
                    <span className="text-white">Airport Transfers</span>
                  </h1>
                  
                  <p className="text-base md:text-lg lg:text-xl mb-8 text-white/70 max-w-md leading-relaxed">
                    Dive deeper into travel around the world and experience the ease of booking worldwide airport transfers through one platform.
                  </p>

                  <div className="flex flex-wrap gap-4 lg:gap-6">
                    <div className="flex items-center gap-2 text-white/60">
                      <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                      <span className="text-sm">24/7 Service</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/60">
                      <div className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
                      <span className="text-sm">Professional Drivers</span>
                    </div>
                    <div className="flex items-center gap-2 text-white/60">
                      <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                      <span className="text-sm">Luxury Fleet</span>
                    </div>
                  </div>
                </div>

                {/* Right Side - Compact Booking Form (45% width on desktop) */}
                <div className="lg:w-[40%] lg:max-w-md animate-scale-in flex-shrink-0">
                  <TransferBookingForm 
                    variant="compact" 
                    showPromoCode={false}
                    onSearchResults={setSearchResults}
                  />
                </div>
              </div>
            </div>
          </div>

        </section>

        {/* Vehicle Selection Section - Appears after search */}
        {searchResults && (
          <VehicleSelectionSection 
            searchData={searchResults} 
            onClose={() => setSearchResults(null)}
          />
        )}

        {/* Featured Tours Section */}
        {featuredTours.length > 0 && (
          <section className="py-24 bg-background relative">
            <div className="container mx-auto px-4">
              <AnimatedSection className="text-center mb-16">
                <VIPBadge variant="gold" className="mb-4">Premium Experiences</VIPBadge>
                <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 text-gradient-gold">
                  Discover VIP Tours
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Exclusive tours and adventures curated for the discerning traveler
                </p>
              </AnimatedSection>

              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
                {featuredTours.map((tour, index) => (
                  <AnimatedSection key={tour.id} delay={index * 100}>
                    <TourCard tour={tour} />
                  </AnimatedSection>
                ))}
              </div>

              <AnimatedSection className="text-center">
                <Link to="/tours">
                  <Button size="lg" className="bg-gradient-gold text-white hover:opacity-90 shadow-gold group px-8 py-6 text-lg">
                    View All Tours
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </AnimatedSection>
            </div>
          </section>
        )}

        {/* Who We Are Section - VIP Dark */}
        <section className="py-24 bg-vip-dark relative overflow-hidden">
          <GoldenParticles count={15} />
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <AnimatedSection animation="slide-left" className="space-y-6">
                <VIPBadge variant="premium">About TROPUNCA VIP</VIPBadge>
                <h2 className="text-4xl md:text-5xl font-bold text-white">
                  <span className="text-gradient-gold">Security</span> & Confidence
                </h2>
                <p className="text-lg text-white/80 leading-relaxed">
                  TROPUNCA VIP is your exclusive transfer and tour service in Punta Cana and throughout the Dominican Republic. We combine professional service with local expertise to create unforgettable Caribbean VIP experiences.
                </p>
                <p className="text-lg text-white/80 leading-relaxed">
                  Our team of experienced, bilingual drivers and tour guides ensure your journey is comfortable, safe, and memorable. Whether you need airport transfers or exciting tours, we're here to make your Dominican vacation exceptional.
                </p>
                <Link to="/about">
                  <Button size="lg" className="bg-gradient-gold text-white hover:opacity-90 shadow-gold group">
                    Learn More About Us
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </AnimatedSection>

              <AnimatedSection animation="slide-right">
                <div className="grid grid-cols-2 gap-6">
                  <Card className="text-center p-6 bg-white/5 border-primary/30 backdrop-blur-sm hover:border-primary/50 transition-all hover:-translate-y-2 group">
                    <div className="w-16 h-16 bg-gradient-gold rounded-full flex items-center justify-center mx-auto mb-4 group-hover:animate-pulse-glow">
                      <Users className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-3xl font-bold mb-2 text-white"><AnimatedCounter end={500} suffix="+" /></h3>
                    <p className="text-white/70">Happy Customers</p>
                  </Card>

                  <Card className="text-center p-6 bg-white/5 border-primary/30 backdrop-blur-sm hover:border-primary/50 transition-all hover:-translate-y-2 group">
                    <div className="w-16 h-16 bg-gradient-gold rounded-full flex items-center justify-center mx-auto mb-4 group-hover:animate-pulse-glow">
                      <Award className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-3xl font-bold mb-2 text-white"><AnimatedCounter end={10} suffix="+" /></h3>
                    <p className="text-white/70">Years Experience</p>
                  </Card>

                  <Card className="text-center p-6 bg-white/5 border-primary/30 backdrop-blur-sm hover:border-primary/50 transition-all hover:-translate-y-2 group">
                    <div className="w-16 h-16 bg-gradient-gold rounded-full flex items-center justify-center mx-auto mb-4 group-hover:animate-pulse-glow">
                      <Headphones className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-3xl font-bold mb-2 text-white">24/7</h3>
                    <p className="text-white/70">Support Available</p>
                  </Card>

                  <Card className="text-center p-6 bg-white/5 border-primary/30 backdrop-blur-sm hover:border-primary/50 transition-all hover:-translate-y-2 group">
                    <div className="w-16 h-16 bg-gradient-gold rounded-full flex items-center justify-center mx-auto mb-4 group-hover:animate-pulse-glow">
                      <Shield className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-3xl font-bold mb-2 text-white">100%</h3>
                    <p className="text-white/70">Safe & Insured</p>
                  </Card>
                </div>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* Why Choose Us Section */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <AnimatedSection className="text-center mb-16">
              <VIPBadge variant="gold" className="mb-4">Our Advantages</VIPBadge>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gradient-gold">Why Choose TROPUNCA VIP</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Experience the difference with our premium VIP service
              </p>
            </AnimatedSection>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                { icon: Users, title: "Professional Drivers", desc: "Bilingual, licensed, and experienced drivers who know Punta Cana inside and out." },
                { icon: Car, title: "Luxury Fleet", desc: "Premium, comfortable, and air-conditioned vehicles for your perfect journey." },
                { icon: Headphones, title: "24/7 VIP Support", desc: "Always available for your needs, day or night, 365 days a year." },
                { icon: DollarSign, title: "Transparent Pricing", desc: "Competitive rates with no hidden fees. What you see is what you pay." },
                { icon: CheckCircle2, title: "Easy Booking", desc: "Simple online reservation system with instant confirmation." },
                { icon: Shield, title: "Safety First", desc: "Fully insured and certified for your peace of mind and safety." },
              ].map((item, index) => (
                <AnimatedSection key={index} delay={index * 100}>
                  <Card className="p-8 hover:shadow-gold transition-all hover:-translate-y-2 group border-transparent hover:border-primary/30">
                    <div className="w-16 h-16 bg-gradient-gold rounded-2xl flex items-center justify-center mb-6 group-hover:animate-pulse-glow transition-all">
                      <item.icon className="h-8 w-8 text-white" />
                    </div>
                    <h3 className="text-2xl font-bold mb-4">{item.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{item.desc}</p>
                  </Card>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>

        {/* Our Services Section */}
        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <AnimatedSection className="text-center mb-16">
              <VIPBadge variant="gold" className="mb-4">What We Offer</VIPBadge>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gradient-gold">Our VIP Services</h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Comprehensive luxury transportation and tour services for your perfect vacation
              </p>
            </AnimatedSection>

            <div className="grid lg:grid-cols-2 gap-8">
              <AnimatedSection animation="slide-left">
                <Card className="overflow-hidden group hover:shadow-gold transition-all border-transparent hover:border-primary/30">
                  <div className="relative h-80">
                    <img 
                      src={bavaroBeach} 
                      alt="VIP Transfers"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                      <div className="w-16 h-16 bg-gradient-gold rounded-full flex items-center justify-center mb-4 shadow-gold">
                        <Plane className="h-8 w-8" />
                      </div>
                      <h3 className="text-3xl font-bold mb-3">VIP Airport Transfers</h3>
                      <p className="text-white/90 mb-6 leading-relaxed">
                        Premium transportation from/to all Dominican airports. Door-to-door service with flight tracking and meet & greet.
                      </p>
                      <Link to="/transfers">
                        <Button size="lg" className="bg-gradient-gold text-white hover:opacity-90 shadow-gold group">
                          Book Transfer
                          <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </Card>
              </AnimatedSection>

              <AnimatedSection animation="slide-right">
                <Card className="overflow-hidden group hover:shadow-gold transition-all border-transparent hover:border-primary/30">
                  <div className="relative h-80">
                    <img 
                      src={bavaroBeach} 
                      alt="VIP Tours"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                      <div className="w-16 h-16 bg-gradient-gold rounded-full flex items-center justify-center mb-4 shadow-gold">
                        <Palmtree className="h-8 w-8" />
                      </div>
                      <h3 className="text-3xl font-bold mb-3">Exclusive Tours & Excursions</h3>
                      <p className="text-white/90 mb-6 leading-relaxed">
                        Discover the beauty of the Dominican Republic with our curated VIP tours and exclusive experiences.
                      </p>
                      <Link to="/tours">
                        <Button size="lg" className="bg-gradient-gold text-white hover:opacity-90 shadow-gold group">
                          Explore Tours
                          <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </Card>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* Dominican Republic Airports Section */}
        <section className="py-24 bg-vip-dark relative overflow-hidden">
          <GoldenParticles count={10} />
          <div className="container mx-auto px-4 relative z-10">
            <AnimatedSection className="text-center mb-16">
              <VIPBadge variant="premium" className="mb-4">We Serve All Airports</VIPBadge>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                Dominican Republic <span className="text-gradient-gold">Airports</span>
              </h2>
              <p className="text-xl text-white/70 max-w-3xl mx-auto">
                Professional VIP transfer service from and to all major Dominican Republic airports
              </p>
            </AnimatedSection>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {airports.map((airport, index) => (
                <AnimatedSection key={airport.code} delay={index * 100}>
                  <Card className={`p-6 bg-white/5 border-primary/30 backdrop-blur-sm hover:border-primary transition-all hover:-translate-y-2 ${airport.popular ? 'ring-2 ring-primary shadow-gold' : ''}`}>
                    {airport.popular && (
                      <VIPBadge variant="exclusive" size="sm" className="mb-4">Most Popular</VIPBadge>
                    )}
                    <div className="flex items-start space-x-4">
                      <div className="w-14 h-14 bg-gradient-gold rounded-full flex items-center justify-center flex-shrink-0">
                        <Plane className="h-7 w-7 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <Badge variant="outline" className="font-bold border-primary text-primary">{airport.code}</Badge>
                          <Clock className="h-4 w-4 text-primary/70" />
                          <span className="text-sm text-white/70">{airport.transferTime}</span>
                        </div>
                        <h3 className="text-lg font-bold mb-2 text-white">{airport.name}</h3>
                        <p className="text-sm text-white/60">{airport.description}</p>
                      </div>
                    </div>
                  </Card>
                </AnimatedSection>
              ))}
            </div>

            <AnimatedSection className="mt-12 text-center">
              <Link to="/transfers">
                <Button size="lg" className="bg-gradient-gold text-white hover:opacity-90 shadow-gold group px-8 py-6 text-lg">
                  Book Your VIP Transfer Now
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </AnimatedSection>
          </div>
        </section>

        {/* Testimonials Section */}
        {featuredTestimonials.length > 0 && (
          <section className="py-24 bg-background">
            <div className="container mx-auto px-4">
              <AnimatedSection className="text-center mb-16">
                <VIPBadge variant="gold" className="mb-4">Client Reviews</VIPBadge>
                <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gradient-gold">What Our VIP Guests Say</h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Real experiences from travelers who trusted us with their journey
                </p>
              </AnimatedSection>

              <div className="grid md:grid-cols-3 gap-8">
                {featuredTestimonials.map((testimonial, index) => (
                  <AnimatedSection key={testimonial.id} delay={index * 150}>
                    <Card className="p-8 hover:shadow-gold transition-all hover:-translate-y-2 border-transparent hover:border-primary/30">
                      <div className="flex items-center space-x-1 mb-4">
                        {[...Array(5)].map((_, i) => (
                          <Star 
                            key={i} 
                            className={`h-5 w-5 ${i < testimonial.rating ? 'fill-primary text-primary' : 'text-gray-300'}`}
                          />
                        ))}
                      </div>
                      <p className="text-muted-foreground leading-relaxed mb-6 italic">
                        "{testimonial.comment}"
                      </p>
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-gradient-gold rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-lg">
                            {testimonial.customer_name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="font-bold">{testimonial.customer_name}</p>
                          <p className="text-sm text-muted-foreground">{testimonial.location}</p>
                        </div>
                      </div>
                    </Card>
                  </AnimatedSection>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* FAQ Section */}
        <section className="py-24 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <AnimatedSection className="text-center mb-16">
                <VIPBadge variant="gold" className="mb-4">Help Center</VIPBadge>
                <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gradient-gold">Frequently Asked Questions</h2>
                <p className="text-xl text-muted-foreground">
                  Everything you need to know about our VIP services
                </p>
              </AnimatedSection>

              <AnimatedSection>
                <Accordion type="single" collapsible className="space-y-4">
                  {[
                    { q: "How do I book a VIP transfer?", a: "Simply use our booking form above, select your pickup and destination, choose the number of passengers, and click \"Search Available Transfers\". You can then select your preferred vehicle type and complete the booking online." },
                    { q: "What's included in the transfer price?", a: "All our transfers include door-to-door VIP service, professional bilingual driver, meet & greet at the airport, flight tracking, all taxes, and tolls. There are no hidden fees." },
                    { q: "Do you offer child seats?", a: "Yes, we provide child seats and booster seats upon request at no additional charge. Please inform us when making your reservation about the number and type of child seats you need." },
                    { q: "Can I cancel or modify my booking?", a: "Yes, you can cancel or modify your booking up to 24 hours before your scheduled pickup time for a full refund. Please contact our VIP customer service team for assistance." },
                    { q: "How far in advance should I book?", a: "We recommend booking at least 24-48 hours in advance to ensure availability, especially during peak season. However, we also accept last-minute bookings subject to availability." },
                    { q: "Do drivers speak English?", a: "Yes, all our VIP drivers are bilingual and speak both English and Spanish fluently. We can also arrange drivers who speak other languages upon request." },
                    { q: "What if my flight is delayed?", a: "We track all flights in real-time, so if your flight is delayed, we'll adjust your pickup time accordingly at no extra charge. Your driver will be there waiting for you whenever you arrive." },
                  ].map((faq, index) => (
                    <AccordionItem key={index} value={`item-${index}`} className="border rounded-lg px-6 bg-background hover:shadow-gold transition-shadow">
                      <AccordionTrigger className="text-left hover:text-primary">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </AnimatedSection>

              <AnimatedSection className="text-center mt-12">
                <p className="text-muted-foreground mb-4">Still have questions?</p>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white group">
                    Contact Our VIP Team
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </AnimatedSection>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section 
          className="relative py-32 bg-cover bg-center overflow-hidden"
          style={{ backgroundImage: `url(${bavaroBeach})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-black/95 via-black/85 to-primary/40" />
          <GoldenParticles count={25} />
          
          <div className="relative z-10 container mx-auto px-4 text-center text-white">
            <AnimatedSection>
              <Crown className="h-16 w-16 mx-auto mb-6 text-primary animate-float" />
              <h2 className="text-4xl md:text-6xl font-bold mb-6">
                Ready for Your <span className="text-gradient-gold">VIP Experience</span>?
              </h2>
              <p className="text-xl md:text-2xl mb-12 max-w-3xl mx-auto text-white/80">
                Experience luxury and reliability with TROPUNCA VIP.<br />
                Your Caribbean adventure starts here.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
                <Link to="/transfers">
                  <Button size="lg" className="bg-gradient-gold text-white hover:opacity-90 shadow-gold text-lg px-12 py-6 h-auto group">
                    Book VIP Transfer Now
                    <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/contact">
                  <Button size="lg" variant="outline" className="glass border-primary/50 text-white hover:bg-primary/20 text-lg px-12 py-6 h-auto backdrop-blur-sm">
                    Contact Us
                  </Button>
                </Link>
              </div>

              <div className="flex flex-col sm:flex-row gap-8 justify-center items-center text-white/80">
                <div className="flex items-center space-x-2">
                  <Phone className="h-5 w-5 text-primary" />
                  <span className="font-medium">+1 (809) 123-4567</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="h-5 w-5 text-primary" />
                  <span className="font-medium">info@tropunca.com</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Globe className="h-5 w-5 text-primary" />
                  <span className="font-medium">24/7 VIP Available</span>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default HomePage;
